import { HTTPResponse } from '@/class/HTTPResponse';
import { getAuthToken, notifyUnauthorized } from './authStorage';

const getBaseUrl = (): string => process.env.REACT_APP_API_ENDPOINT || 'http://localhost:8000/api';

const isAuthEndpoint = (url: string): boolean => /\/(store\/auth|system-admin\/auth)$/.test(url);

const buildHeaders = (includeJsonContentType = false): HeadersInit => {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  if (includeJsonContentType) {
    headers['Content-Type'] = 'application/json';
  }

  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const handleResponse = async (response: Response, url: string): Promise<HTTPResponse> => {
  if (response.status === 401 && !isAuthEndpoint(url)) {
    notifyUnauthorized();
    throw new Error('認証の有効期限が切れました。再度ログインしてください。');
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status} (URL: ${url})`);
  }

  return response.json();
};

export const fetchData = async (endpoint: string): Promise<HTTPResponse> => {
  const url = `${getBaseUrl()}/${endpoint}`;
  try {
    const response = await fetch(url, {
      headers: buildHeaders(),
    });

    return handleResponse(response, url);
  } catch (error: any) {
    console.error(`Error fetching ${url}:`, error);
    throw error;
  }
};

export const postData = async (endpoint: string, body: any): Promise<HTTPResponse> => {
  const url = `${getBaseUrl()}/${endpoint}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: buildHeaders(true),
      body: JSON.stringify(body),
    });

    return handleResponse(response, url);
  } catch (error: any) {
    console.error(`Error posting to ${url}:`, error);
    throw error;
  }
};

export const postFormData = async (endpoint: string, formData: FormData): Promise<HTTPResponse> => {
  const url = `${getBaseUrl()}/${endpoint}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: buildHeaders(false),
      body: formData,
    });

    return handleResponse(response, url);
  } catch (error: any) {
    console.error(`Error posting to ${url}:`, error);
    throw error;
  }
};

export const putData = async (endpoint: string, body: any): Promise<HTTPResponse> => {
  const url = `${getBaseUrl()}/${endpoint}`;

  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers: buildHeaders(true),
      body: JSON.stringify(body),
    });

    return handleResponse(response, url);
  } catch (error: any) {
    console.error(`Error posting to ${url}:`, error);
    throw error;
  }
};

export const deleteData = async (endpoint: string): Promise<HTTPResponse> => {
  const url = `${getBaseUrl()}/${endpoint}`;

  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: buildHeaders(true),
    });

    return handleResponse(response, url);
  } catch (error: any) {
    console.error(`Error deleting ${url}:`, error);
    throw error;
  }
};
